import { useEffect } from "react";
import { send_request } from "../../../utils/Request";
import useAgentStore from "../useAgentStore";
import {
  agentDraftFromApi,
  normalizeReviewTaskUsers,
} from "./agentConfigDraftModel";
import { KNOWLEDGE_INDEX_DEFAULT_STATUS } from "./knowledgeStatus";
import { mapToolCatalogToCategories } from "./toolCatalog";

const mapDatabaseTables = (res) => {
  const v2 = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res)
    ? res
    : [];
  return v2.map((t) => ({
    databaseId: t.tableId,
    tableId: t.tableId,
    name: t.tableName,
    tableName: t.tableName,
  }));
};

export function useAgentConfigBootstrap({
  open,
  agentId,
  loaded,
  applyDraftToState,
  setLoaded,
  setPublicShareLink,
  setPublicShareLinkError,
  loadLatestPublicShareLink,
  setKnowledgeIndexStatus,
  setLoadingToolCatalog,
  setToolCategories,
  setLoadingWorkflowOptions,
  setWorkflowOptions,
  setLoadingAgentOptions,
  setAgentOptions,
  setGlobalVarPlaceholders,
  setLoadingDatabases,
  setDatabaseList,
  setLoadingReviewTaskEligibleUsers,
  setReviewTaskEligibleUsers,
  setAllAgents,
}) {
  useEffect(() => {
    let ignore = false;
    if (open && !loaded) {
      if (agentId) {
        useAgentStore
          .getState()
          .fetchAgent(agentId)
          .then((data) => {
            if (ignore) return;
            if (data) {
              applyDraftToState(agentDraftFromApi(data));
              setPublicShareLinkError("");
              void loadLatestPublicShareLink({ ignore: () => ignore });
            } else {
              setPublicShareLink(null);
              setPublicShareLinkError("");
              setKnowledgeIndexStatus({ ...KNOWLEDGE_INDEX_DEFAULT_STATUS });
            }
            setLoaded(true);
          });
      } else {
        setLoaded(true);
      }
    }
    return () => {
      ignore = true;
    };
  }, [
    open,
    agentId,
    loaded,
    applyDraftToState,
    loadLatestPublicShareLink,
    setKnowledgeIndexStatus,
    setLoaded,
    setPublicShareLink,
    setPublicShareLinkError,
  ]);

  useEffect(() => {
    let ignore = false;
    if (open) {
      setLoadingToolCatalog(true);
      setLoadingWorkflowOptions(true);
      setLoadingAgentOptions(true);
      setLoadingDatabases(true);
      setLoadingReviewTaskEligibleUsers(true);

      const toolCatalogPromise = useAgentStore
        .getState()
        .fetchToolCatalog()
        .then((catalog) => mapToolCatalogToCategories(catalog))
        .catch(() => []);
      const workflowOptionsPromise = useAgentStore
        .getState()
        .fetchWorkflowOptions()
        .then((options) => (Array.isArray(options) ? options : []))
        .catch(() => []);
      const agentOptionsPromise = send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      )
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          return list.filter((agent) => agent?.systemAgent !== true);
        })
        .catch(() => []);
      const globalVarsPromise = send_request(
        "component-service/global-variables",
        "",
        {},
        "get"
      )
        .then((res) => {
          const vars = res?.data || [];
          return vars.map((v) => ({ key: `${v.name}.GLOBAL`, type: "text" }));
        })
        .catch(() => []);
      const databaseListPromise = send_request(
        "data-service/v2/table",
        "",
        {},
        "GET"
      )
        .then(mapDatabaseTables)
        .catch(() => []);
      const reviewUsersPromise = send_request(
        "authz-service/get-active-users",
        "",
        "",
        "get"
      )
        .then((res) => normalizeReviewTaskUsers(res?.data))
        .catch(() => []);
      const allAgentsPromise = useAgentStore
        .getState()
        .refreshAgents()
        .then(() =>
          useAgentStore
            .getState()
            .agents.filter((agent) => agent.agentId !== agentId)
        )
        .catch(() => []);

      Promise.allSettled([
        toolCatalogPromise,
        workflowOptionsPromise,
        agentOptionsPromise,
        globalVarsPromise,
        databaseListPromise,
        reviewUsersPromise,
        allAgentsPromise,
      ]).then((results) => {
        if (ignore) return;
        const valueAt = (index, fallback) =>
          results[index]?.status === "fulfilled"
            ? results[index].value
            : fallback;
        setToolCategories(valueAt(0, []));
        setWorkflowOptions(valueAt(1, []));
        setAgentOptions(valueAt(2, []));
        setGlobalVarPlaceholders(valueAt(3, []));
        setDatabaseList(valueAt(4, []));
        setReviewTaskEligibleUsers(valueAt(5, []));
        setAllAgents(valueAt(6, []));
        setLoadingToolCatalog(false);
        setLoadingWorkflowOptions(false);
        setLoadingAgentOptions(false);
        setLoadingDatabases(false);
        setLoadingReviewTaskEligibleUsers(false);
      });
    }
    return () => {
      ignore = true;
    };
  }, [
    open,
    agentId,
    setAgentOptions,
    setAllAgents,
    setDatabaseList,
    setGlobalVarPlaceholders,
    setLoadingAgentOptions,
    setLoadingDatabases,
    setLoadingReviewTaskEligibleUsers,
    setLoadingToolCatalog,
    setLoadingWorkflowOptions,
    setReviewTaskEligibleUsers,
    setToolCategories,
    setWorkflowOptions,
  ]);
}
